import {
  appendSubmissionEvent,
  createTransmission,
  createTransmissionRecord,
  enrichRecordWithTransmission,
  findChildTransmissions,
  findEventsByTransmission,
  findRecordsByClient,
  findRecordsByTransmission,
  findTransmission,
  findTransmissionsByAdmin,
  updateTransmission,
} from './mockStore';
import { scheduleLifecycleAdvancement } from './lifecycleSimulator';

// Deterministic 64-char hex digest for demo display. Not cryptographic — a
// simple LCG (MINSTD) seeded from the id produces a stable, realistic-looking
// payload hash per transmission.
const fakePayloadHash = (seed) => {
  let state = 0;
  for (let i = 0; i < seed.length; i += 1) {
    state = (state * 31 + seed.charCodeAt(i)) % 2147483647;
  }
  let x = state + 1;
  let hex = '';
  while (hex.length < 64) {
    x = (x * 48271) % 2147483647;
    hex += (x % 16).toString(16);
  }
  return `sha256:${hex}`;
};

const paginate = (rows, limit, offset) => {
  const safeLimit = Number.isFinite(limit) ? limit : rows.length;
  const safeOffset = Number.isFinite(offset) ? offset : 0;
  return {
    records: rows.slice(safeOffset, safeOffset + safeLimit),
    totalCount: rows.length,
    limit: safeLimit,
    offset: safeOffset,
  };
};

const buildMockValidationSummary = (recordCount) => ({
  errorCount: 0,
  warningCount: Math.min(3, Math.floor(recordCount / 2)),
  recordCount,
  __typename: 'CarfValidationSummary',
});

const resolvers = {
  GetCarfTransmissions: ({ adminAccountId }) => ({
    data: {
      getCarfTransmissions: findTransmissionsByAdmin(adminAccountId),
    },
  }),

  GetCarfTransmission: ({ id }) => {
    const tx = findTransmission(id);
    if (!tx) return { data: { getCarfTransmission: null } };
    return {
      data: {
        getCarfTransmission: {
          ...tx,
          childTransmissions: findChildTransmissions(id).map((c) => ({
            id: c.id,
            status: c.status,
            jurisdiction: c.jurisdiction,
            period: c.period,
            __typename: 'CarfTransmission',
          })),
        },
      },
    };
  },

  GetCarfTransmissionRecords: ({ transmissionId, limit, offset }) => ({
    data: {
      getCarfTransmissionRecords: {
        ...paginate(findRecordsByTransmission(transmissionId), limit, offset),
        __typename: 'CarfTransmissionRecordPage',
      },
    },
  }),

  GetCarfSubmissionEvents: ({ transmissionId }) => ({
    data: {
      getCarfSubmissionEvents: findEventsByTransmission(transmissionId),
    },
  }),

  GetCarfClientTransmissionRecords: ({ clientAccountId, limit, offset }) => {
    const enriched = findRecordsByClient(clientAccountId).map(
      enrichRecordWithTransmission,
    );
    return {
      data: {
        getCarfClientTransmissionRecords: {
          ...paginate(enriched, limit, offset),
          __typename: 'CarfTransmissionRecordPage',
        },
      },
    };
  },

  CreateCarfTransmissionDraft: (variables) => {
    const transmission = createTransmission({
      adminAccountId: variables.adminAccountId,
      jurisdiction: variables.jurisdiction,
      period: variables.period,
      messageTypeIndic: variables.messageTypeIndic,
      parentTransmissionId: variables.parentTransmissionId,
      status: 'draft',
    });
    appendSubmissionEvent({
      transmissionId: transmission.id,
      kind: 'draft_created',
      message: 'Draft transmission created',
    });
    return { data: { createCarfTransmissionDraft: transmission } };
  },

  BuildCarfTransmissionRecords: ({ transmissionId, scopedClientCount }) => {
    const transmission = findTransmission(transmissionId);
    if (!transmission) {
      return { data: { buildCarfTransmissionRecords: null } };
    }
    let existing = findRecordsByTransmission(transmissionId);
    // For new (non-correction) drafts, materialize one CARF record per client in
    // the admin's client list (scopedClientCount = the cached client-list total),
    // so the wizard estimate, validation and table all match the client list.
    if (existing.length === 0 && !transmission.parentTransmissionId) {
      const clientCount = Math.max(0, scopedClientCount || 0);
      for (let i = 0; i < clientCount; i += 1) {
        createTransmissionRecord({
          transmissionId,
          clientAccountId: `client-${i + 1}`,
          clientName: `Demo Client ${i + 1}`,
        });
      }
      existing = findRecordsByTransmission(transmissionId);
    }
    const recordCount = existing.length;
    const updated = updateTransmission(transmissionId, {
      recordCount,
    });
    return {
      data: {
        buildCarfTransmissionRecords: {
          id: updated.id,
          status: updated.status,
          recordCount: updated.recordCount,
          __typename: 'CarfTransmission',
        },
      },
    };
  },

  ValidateCarfTransmission: ({ transmissionId }) => {
    const transmission = findTransmission(transmissionId);
    if (!transmission) {
      return { data: { validateCarfTransmission: null } };
    }
    const summary = buildMockValidationSummary(transmission.recordCount || 0);
    const updated = updateTransmission(transmissionId, {
      status: 'validated',
      validationSummary: summary,
    });
    appendSubmissionEvent({
      transmissionId,
      kind: 'validated',
      message: `Validated ${summary.recordCount} records`,
    });
    return {
      data: {
        validateCarfTransmission: {
          id: updated.id,
          status: updated.status,
          validationSummary: summary,
          __typename: 'CarfTransmission',
        },
      },
    };
  },

  TransmitCarfTransmission: ({ transmissionId, confirmationId }) => {
    const transmission = findTransmission(transmissionId);
    if (!transmission) {
      return { data: { transmitCarfTransmission: null } };
    }
    const submittedAt = new Date().toISOString();

    if (confirmationId) {
      // Manual submission: authority reference provided directly; mark as accepted
      const updated = updateTransmission(transmissionId, {
        status: 'accepted',
        submittedAt,
        submittedBy: 'mock-user',
        payloadHash: fakePayloadHash(transmissionId),
        payloadUri: `mock://carf-payloads/${transmissionId}.xml`,
        authorityReference: confirmationId,
      });
      appendSubmissionEvent({
        transmissionId,
        kind: 'submitted',
        message: 'XML submitted manually to tax authority',
      });
      appendSubmissionEvent({
        transmissionId,
        kind: 'accepted',
        message: `Confirmation ID recorded: ${confirmationId}`,
      });
      return {
        data: {
          transmitCarfTransmission: {
            id: updated.id,
            status: updated.status,
            submittedAt: updated.submittedAt,
            submittedBy: updated.submittedBy,
            payloadHash: updated.payloadHash,
            payloadUri: updated.payloadUri,
            authorityReference: updated.authorityReference,
            __typename: 'CarfTransmission',
          },
        },
      };
    }

    const updated = updateTransmission(transmissionId, {
      status: 'submitted',
      submittedAt,
      submittedBy: 'mock-user',
      payloadHash: fakePayloadHash(transmissionId),
      payloadUri: `mock://carf-payloads/${transmissionId}.xml`,
    });
    appendSubmissionEvent({
      transmissionId,
      kind: 'submitted',
      message: 'Submitted to authority',
    });
    scheduleLifecycleAdvancement(transmissionId);
    return {
      data: {
        transmitCarfTransmission: {
          id: updated.id,
          status: updated.status,
          submittedAt: updated.submittedAt,
          submittedBy: updated.submittedBy,
          payloadHash: updated.payloadHash,
          payloadUri: updated.payloadUri,
          authorityReference: updated.authorityReference || null,
          __typename: 'CarfTransmission',
        },
      },
    };
  },

  FileCarfCorrection: ({ parentTransmissionId, correctionRecords }) => {
    const parent = findTransmission(parentTransmissionId);
    if (!parent) {
      return { data: { fileCarfCorrection: null } };
    }
    const correction = createTransmission({
      adminAccountId: parent.adminAccountId,
      jurisdiction: parent.jurisdiction,
      period: parent.period,
      messageTypeIndic: 'CARF702',
      parentTransmissionId: parent.id,
      status: 'draft',
    });
    correctionRecords.forEach((r) => {
      createTransmissionRecord({
        transmissionId: correction.id,
        clientAccountId: r.clientAccountId,
        docTypeIndic: r.docTypeIndic,
        correlatedRecordId: r.correlatedRecordId,
        correlatedDocRefId: r.correlatedDocRefId,
      });
    });
    appendSubmissionEvent({
      transmissionId: correction.id,
      kind: 'correction_filed',
      message: `Correction drafted for parent ${parent.messageRefId}`,
    });
    return {
      data: {
        fileCarfCorrection: {
          id: correction.id,
          status: correction.status,
          parentTransmissionId: correction.parentTransmissionId,
          messageRefId: correction.messageRefId,
          __typename: 'CarfTransmission',
        },
      },
    };
  },
};

export default resolvers;
